import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"
import type { Pagination as PaginationType } from "@/types"

type Props = {
  pagination: PaginationType
  baseUrl?: string
}

export function Pagination({ pagination, baseUrl = "/samples" }: Props) {
  const { page, pages, count } = pagination

  if (pages <= 1) return null

  const goToPage = (newPage: number) => {
    const params = new URLSearchParams(window.location.search)
    if (newPage > 1) {
      params.set("page", String(newPage))
    } else {
      params.delete("page")
    }
    router.get(`${baseUrl}?${params.toString()}`, {}, { preserveState: true, preserveScroll: true })
  }

  const pageNumbers = () => {
    const numbers: (number | "...")[] = []
    const showPages = 5

    if (pages <= showPages + 2) {
      for (let i = 1; i <= pages; i++) numbers.push(i)
    } else {
      numbers.push(1)
      if (page > 3) numbers.push("...")

      const start = Math.max(2, page - 1)
      const end = Math.min(pages - 1, page + 1)
      for (let i = start; i <= end; i++) numbers.push(i)

      if (page < pages - 2) numbers.push("...")
      numbers.push(pages)
    }

    return numbers
  }

  return (
    <div className="flex items-center justify-between mt-8">
      <p className="text-sm text-muted-foreground font-mono">
        {count} samples
      </p>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          disabled={page === 1}
          onClick={() => goToPage(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {pageNumbers().map((num, i) =>
          num === "..." ? (
            <span key={`ellipsis-${i}`} className="px-2 text-muted-foreground">...</span>
          ) : (
            <Button
              key={num}
              variant={num === page ? "default" : "ghost"}
              size="icon"
              onClick={() => goToPage(num)}
              className="font-mono"
            >
              {num}
            </Button>
          )
        )}

        <Button
          variant="ghost"
          size="icon"
          disabled={page === pages}
          onClick={() => goToPage(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
